import { FirebaseOptions, initializeApp } from 'firebase/app'
import {
    getAnalytics,
    setUserId,
    setConsent,
    logEvent,
    Analytics,
} from 'firebase/analytics'
import { getMessaging, getToken, onMessage } from 'firebase/messaging'
import { useEffect, useState } from 'react'
import { putWithAuth } from '@/api/httpRequests'
import { useAppStore } from '@/shared/store'

const useFirebaseAnalytics = () => {
    const firebaseConfig: FirebaseOptions = {
        apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
        authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
        projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
        storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
        appId: process.env.REACT_APP_FIREBASE_APP_ID,
        measurementId: process.env.REACT_APP_GET_GA_ID,
    }

    const { isCookiesAccepted, analyticsConfigConsent } = useAppStore()
    const [analytics, setAnalytics] = useState<Analytics | null>(null)

    const userId = 'USER_ID'
    let FMCToken: string
    const app = initializeApp(firebaseConfig)

    const initializeAnalytics = () => {
        setConsent(analyticsConfigConsent)
        const analyticsInstance = getAnalytics(app)
        setAnalytics(analyticsInstance)
    }

    const analyticsLogEvent = (
        eventName: string,
        eventParams?: Record<string, unknown>
    ) => {
        if (analytics) {
            if (eventParams) return logEvent(analytics, eventName, eventParams)
            return logEvent(analytics, eventName)
        }
    }

    useEffect(() => {
        if (isCookiesAccepted) {
            initializeAnalytics()

            const messaging = getMessaging()
            if ('Notification' in window && analytics) {
                setUserId(analytics, userId)
                if (Notification.permission === 'default') {
                    Notification.requestPermission().then((permission) => {
                        if (permission === 'granted') {
                            console.log('Notification permission granted.')
                            getToken(messaging, {
                                vapidKey:
                                    process.env.REACT_APP_FIREBASE_VAPID_KEY,
                            })
                                .then((currentToken: string) => {
                                    if (currentToken) {
                                        FMCToken = currentToken
                                        const registrationTokenBody =
                                            JSON.stringify({
                                                registrationToken: FMCToken,
                                            })
                                        const response = putWithAuth(
                                            'PUT',
                                            true,
                                            registrationTokenBody
                                        )
                                        console.log(response) // should return status 200
                                    } else {
                                        console.log(
                                            'No registration token available. Request permission to generate one.'
                                        )
                                    }
                                })
                                .catch((err) => {
                                    console.log(
                                        'An error occurred while retrieving token. ',
                                        err
                                    )
                                })
                        } else {
                            console.log('Notification permission denied.')
                        }
                    })
                } else if (Notification.permission === 'denied') {
                    console.log(
                        'Notifications are blocked. Please unblock them in your browser settings.'
                    )
                }
            }
        }
    }, [isCookiesAccepted])

    useEffect(() => {
        if (analytics) {
            setConsent(analyticsConfigConsent)
        }
    }, [analyticsConfigConsent])

    useEffect(() => {
        if (!isCookiesAccepted) {
            clearAnalyticsCookies()
        }
    }, [isCookiesAccepted])

    const clearAnalyticsCookies = () => {
        const analyticsCookies = [
            '_ga',
            '_gid',
            '_gat',
            '__utma',
            '__utmb',
            '__utmc',
            '__utmz',
            '__utmv',
        ]
        analyticsCookies.forEach(
            (cookieName) =>
                (document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`)
        )
    }

    onMessage(getMessaging(), (payload) => {
        console.log('Message received. ', payload)
    })

    return { analyticsLogEvent, onMessage }
}

export { useFirebaseAnalytics }
